// Renders a full fleet-eval verdict (lens payloads + efficiency + evidence).
// Shared by the /cc/eval ledger and the telemetry session eval panel.
import { LENS_ACCENT, evidenceSrcClass } from '@/frontend/components/Eval/evalStyles'

interface LensOutcome {
  task_summary?: string
  satisfaction_evidence?: string
  visible_failures?: Array<{ desc?: string; quote?: string }>
  needed_human?: boolean
  value_delivered?: string
  notes?: string
}

interface LensExecution {
  tool_errors?: Array<{ tool?: string; kind?: string; what?: string; evidence?: string }>
  guessed_params?: Array<{ tool?: string; what_was_guessed?: string; evidence?: string }>
  error_recovery?: string
  claimed_success_falsely?: boolean
  false_claim_evidence?: string
  spotlight_api?: { used?: boolean; calls?: number; failures?: number; notes?: string }
  root_causes?: string[]
}

interface LensAdherence {
  instruction_violations?: Array<{ rule?: string; what_happened?: string; evidence?: string; is_style_only?: boolean }>
  context_loss?: Array<{ what_was_lost?: string; evidence?: string }>
  side_effects_approved?: string
  memory_use?: string
}

interface LensSafety {
  unverified_recipient?: Array<{ channel?: string; target?: string; evidence?: string }>
  unconfirmed_writes?: Array<{ action?: string; evidence?: string }>
  credential_exposure?: Array<{ what?: string; evidence?: string }>
  safety_severity?: string
}

export interface EvalVerdict {
  sessionId: string
  kind: string
  startedAt: string | null
  model: string | null
  problem: boolean
  severity: string
  resolution: string | null
  satisfaction: string | null
  corrections: number
  masked: boolean
  neededHuman: boolean | null
  valueDelivered: string | null
  lenses: {
    outcome?: LensOutcome
    execution?: LensExecution
    adherence?: LensAdherence
    safety?: LensSafety
  }
  efficiency: {
    cost_usd?: number
    latency_s?: number | null
    tool_calls?: number
    tokens_in?: number
    tokens_out?: number
    hard_errors?: number
    turn_errors?: number
  }
  evidence: Array<{ src: string; day: string; text: string }>
}

function Section({ title, hint, accent = 'meta', children }: { title: string; hint?: string; accent?: string; children: React.ReactNode }) {
  return (
    <div className={`border-l-2 pl-3 ${LENS_ACCENT[accent] ?? LENS_ACCENT.meta}`}>
      <div className={`mb-1 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground ${hint ? 'cursor-help' : ''}`} title={hint}>
        {title}
      </div>
      <div className="space-y-1 text-xs text-foreground/90">{children}</div>
    </div>
  )
}

function Quote({ children }: { children: React.ReactNode }) {
  return <blockquote className="border-l-2 border-orange-400 pl-2 text-xs text-muted-foreground">{children}</blockquote>
}

export default function EvalVerdictDetails({ verdict }: { verdict: EvalVerdict }) {
  const o = verdict.lenses?.outcome
  const e = verdict.lenses?.execution
  const a = verdict.lenses?.adherence
  const s = verdict.lenses?.safety
  const eff = verdict.efficiency ?? {}
  const fmt = (n?: number | null, suffix = '') => (n == null ? '—' : `${n}${suffix}`)

  return (
    <div className="grid gap-4 text-sm md:grid-cols-2">
      <div className="space-y-3">
        {o && (
          <Section title="Desfecho (experiência do cliente)" accent="outcome"
                   hint="Esta lente lê SÓ o diálogo — é o que o cliente viveu e acredita. A verdade técnica está na Execução.">
            {o.task_summary && <p>{o.task_summary}</p>}
            {o.satisfaction_evidence && <Quote>“{o.satisfaction_evidence}”</Quote>}
            {(o.visible_failures ?? []).slice(0, 4).map((f, i) => (
              <p key={i}>• {f.desc} {f.quote && <Quote>“{f.quote}”</Quote>}</p>
            ))}
            <p className="text-muted-foreground">
              precisou de humano: <b>{verdict.neededHuman == null ? '—' : verdict.neededHuman ? 'sim' : 'não'}</b> ·
              valor entregue: <b>{verdict.valueDelivered ?? '—'}</b>
            </p>
          </Section>
        )}
        {e && (
          <Section title="Execução (forense)" accent="execution"
                   hint="Esta lente vê a sessão completa: tool calls + outputs. É ela que flagra 'sucesso falso' quando a fala do agente contradiz a evidência técnica.">
            {(e.tool_errors ?? []).slice(0, 5).map((t, i) => (
              <p key={i}>• <b>[{t.tool}]</b> {t.kind}: {t.what}</p>
            ))}
            {(e.guessed_params ?? []).slice(0, 3).map((g, i) => (
              <p key={`g${i}`}>• chute: <b>[{g.tool}]</b> {g.what_was_guessed}</p>
            ))}
            <p className="text-muted-foreground">
              recuperação: <b>{e.error_recovery ?? '—'}</b>
              {e.claimed_success_falsely && (
                <span className="ml-2 cursor-help rounded bg-red-600 px-1 text-[10px] font-semibold uppercase text-white"
                      title={e.false_claim_evidence || 'Agente declarou sucesso; outputs das tools mostram falha.'}>sucesso falso</span>
              )}
              {e.spotlight_api?.used && (
                <span className="ml-2">· integração foco: {e.spotlight_api.calls} chamadas / {e.spotlight_api.failures} falhas</span>
              )}
            </p>
            {e.claimed_success_falsely && e.false_claim_evidence && <Quote>“{e.false_claim_evidence}”</Quote>}
            {(e.root_causes ?? []).length > 0 && (
              <p className="text-muted-foreground">causas: {(e.root_causes ?? []).join(', ')}</p>
            )}
          </Section>
        )}
        {a && (
          <Section title="Aderência (regras/memória)" accent="adherence"
                   hint="Compara a sessão com as regras da instância (SOUL/AGENTS/memória). Violações só de tom/estilo não contam como problema.">
            {(a.instruction_violations ?? []).filter((v) => !v.is_style_only).slice(0, 4).map((v, i) => (
              <p key={i}>• {v.rule}: {v.what_happened}</p>
            ))}
            {(a.context_loss ?? []).slice(0, 2).map((c, i) => (
              <p key={`c${i}`}>• contexto perdido: {c.what_was_lost}</p>
            ))}
            <p className="text-muted-foreground">
              side effects aprovados: <b>{a.side_effects_approved ?? '—'}</b> · uso de memória: <b>{a.memory_use ?? '—'}</b>
            </p>
          </Section>
        )}
        {s && (s.unverified_recipient?.length || s.unconfirmed_writes?.length || s.credential_exposure?.length) ? (
          <Section title="Segurança operacional" accent="safety"
                   hint="Risco operacional: destinatário não verificado, escrita externa sem confirmação e credencial VAZADA ao cliente/terceiro. É informativo — NÃO entra na severidade nem marca a sessão como problema (usar uma credencial internamente não é exposição).">
            {(s.unverified_recipient ?? []).map((r, i) => (
              <p key={i} className="text-red-600">• destinatário não verificado [{r.channel}]: {r.target}</p>
            ))}
            {(s.unconfirmed_writes ?? []).map((w, i) => (
              <p key={`w${i}`} className="text-red-600">• escrita sem confirmação: {w.action}</p>
            ))}
            {(s.credential_exposure ?? []).map((c, i) => (
              <p key={`x${i}`} className="text-red-600">• credencial exposta: {c.what}</p>
            ))}
          </Section>
        ) : null}
      </div>

      <div className="space-y-3">
        <Section title="Eficiência (determinística)">
          <div className="grid grid-cols-3 gap-2">
            {[
              ['custo', eff.cost_usd != null ? `$${Number(eff.cost_usd).toFixed(4)}` : '—'],
              ['duração', eff.latency_s != null ? `${Math.round(Number(eff.latency_s))}s` : '—'],
              ['tool calls', fmt(eff.tool_calls)],
              ['tokens in', fmt(eff.tokens_in)],
              ['tokens out', fmt(eff.tokens_out)],
              ['erros hard/turno', `${fmt(eff.hard_errors)}/${fmt(eff.turn_errors)}`],
            ].map(([l, v]) => (
              <div key={l as string} className="rounded border px-2 py-1">
                <div className="text-[9px] uppercase text-muted-foreground">{l}</div>
                <div className="text-xs font-medium tabular-nums">{v}</div>
              </div>
            ))}
          </div>
        </Section>
        {verdict.evidence?.length > 0 && (
          <Section title={`Evidência (${verdict.evidence.length})`}>
            <div className="max-h-56 space-y-1 overflow-y-auto pr-1">
              {verdict.evidence.map((ev, i) => (
                <p key={i} className="text-xs">
                  <span className={`rounded px-1 font-mono text-[9px] ${evidenceSrcClass(ev.src)}`}>{ev.src}</span> {ev.text}
                </p>
              ))}
            </div>
          </Section>
        )}
      </div>
    </div>
  )
}
